/**
 * FileDropZone — universal, controlled file-selection control with drag & drop
 * (epic 023, task 02 — Chelsie Aug-14 "Co-op Improvements / File Upload" feedback;
 * whole-surface click target + hover/active feedback — task 025-03, QA #43).
 *
 * A dumb selection control only: no upload logic, no validation, no domain
 * knowledge. Wireable into any file-upload surface purely through
 * `value`/`onChange` (precedent: typeahead.tsx for dedicated form components).
 *
 * - Browse stays a first-class route: a visible native `<input type="file">`,
 *   associated with the visible label via `htmlFor`/`id`. Its `files[0]` (when
 *   present) routes into `onChange`; an empty FileList is a no-op.
 * - Drag & drop: native HTML DnD events on the drop surface — `preventDefault()`
 *   on dragEnter/dragOver (the drop is not allowed otherwise) and always on drop.
 *   Only the first dropped file is taken; non-file drags have an empty FileList
 *   and are ignored. A drag-depth counter guards the active state against the
 *   enter/leave event pairs fired when the drag crosses child elements.
 * - Clear: when a file is selected a name chip shows it plus a clear affordance
 *   that fires `onChange(null)`. When the controlled `value` becomes null the
 *   component resets its own input's displayed filename, so consumers never
 *   need a ref into it.
 * - The whole dashed surface is a second `<label htmlFor={id}>` for the same
 *   input (QA #43): clicking anywhere in it opens the OS file dialog, with
 *   hover/active accent feedback echoing the dragging state. The label
 *   association is the single activation source — there is no component click
 *   handler, so the dialog opens exactly once: HTML label-activation rules do
 *   not forward clicks that target the input itself or interactive children
 *   (the clear affordance).
 * - Keyboard path is the native input: visible, tabbable, and natively
 *   activated (Enter/Space). The surface label adds no tabindex/role, so the
 *   keyboard path is unchanged.
 * - No client-side file validation: bad files surface the server's 400 exactly
 *   like a bad browse selection would.
 *
 * Visual language follows the repo's dropzone convention (the deleted formbits
 * dropzone): rounded dashed border, active accent border while dragging, name
 * chip on `bg-accent-soft text-accent`. Hover echoes the dragging accent
 * (accent border + soft tint); press keeps that accent state and adds a
 * subtle scale-down.
 */

import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { cn } from '../lib/utils';

export interface FileDropZoneProps {
  /** Forwarded to the native input; pairs with `label` for the accessible name. */
  id: string;
  /** Visible form label, rendered as `<label htmlFor={id}>`. */
  label: React.ReactNode;
  /** Currently selected file (controlled single source of truth). */
  value: File | null;
  /** Called with the selected file, or null when the clear affordance is used. */
  onChange: (file: File | null) => void;
  /** Native input `accept` filter (e.g. ".csv"). Not a client-side validation. */
  accept?: string;
  /** Rendered below the control; visibility conditions stay consumer-side. */
  hint?: React.ReactNode;
  /** Copy shown inside the dropzone. */
  dropzoneText?: React.ReactNode;
  /** Extra classes on the root wrapper (consumer-side layout). */
  className?: string;
}

export function FileDropZone({
  id,
  label,
  value,
  onChange,
  accept,
  hint,
  dropzoneText = 'or drag & drop file here',
  className,
}: FileDropZoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  // Enter/leave pairs nest per element as the drag crosses children, so a bare
  // boolean flickers; counting the (bubbling) enter/leave events is the guard.
  const dragDepth = useRef(0);
  const [dragActive, setDragActive] = useState(false);

  // The controlled value is the single source of truth: when the consumer
  // resets it to null (clear affordance, post-upload reset), the native input's
  // displayed filename is cleared here — consumers stay ref-free.
  useEffect(() => {
    if (value === null && inputRef.current && inputRef.current.value !== '') {
      inputRef.current.value = '';
    }
  }, [value]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) onChange(file);
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault(); // required or the subsequent drop is not allowed
    dragDepth.current += 1;
    setDragActive(true);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault(); // required or the subsequent drop is not allowed
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setDragActive(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault(); // always — a dropped file must not open in the browser
    dragDepth.current = 0;
    setDragActive(false);
    const file = e.dataTransfer?.files?.[0]; // first file only; non-file drags are empty
    if (file) onChange(file);
  };

  return (
    <div className={cn('space-y-2', className)}>
      <label htmlFor={id} className="text-xs font-semibold text-ink">
        {label}
      </label>
      <label
        htmlFor={id}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={cn(
          'flex flex-col items-center gap-2 rounded-lg border-2 border-dashed px-4 py-6 text-center transition',
          // NOTE: hover/press use plain token classes — alpha modifiers on
          // these var() tokens (e.g. `bg-accent-soft/40`) are silently dropped
          // by Tailwind (colors lack <alpha-value> in tailwind.config.js), so
          // such classes compile to nothing. Press distinguishes itself from
          // hover with a subtle scale-down. The drag branch below keeps its
          // pre-existing `bg-accent-soft/40` (byte-identical DnD styling).
          'cursor-pointer hover:border-accent hover:bg-accent-soft active:border-accent active:bg-accent-soft active:scale-[0.99]',
          dragActive ? 'border-accent bg-accent-soft/40' : 'border-line-strong bg-paper/50'
        )}
      >
        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-accent-soft text-accent">
          <svg
            aria-hidden="true"
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M8 10V3M5.5 5.5 8 3l2.5 2.5M3 11v1.5A1.5 1.5 0 0 0 4.5 14h7a1.5 1.5 0 0 0 1.5-1.5V11" />
          </svg>
        </div>
        <input
          ref={inputRef}
          id={id}
          type="file"
          accept={accept}
          onChange={handleInputChange}
          className="cursor-pointer text-xs text-muted file:mr-3 file:cursor-pointer file:rounded-md file:border-0 file:bg-surface file:px-3 file:py-1.5 file:text-xs file:font-semibold file:text-accent transition hover:file:bg-line/40"
        />
        <span className="text-xs text-faint">{dropzoneText}</span>
        {value && (
          <div className="inline-flex items-center gap-1.5 rounded-md bg-accent-soft px-2 py-1 font-mono text-xs text-accent">
            <span className="max-w-[16rem] truncate">{value.name}</span>
            <button
              type="button"
              onClick={() => onChange(null)}
              title="Clear selected file"
              className="text-accent/70 transition hover:text-rose"
            >
              ×
            </button>
          </div>
        )}
      </label>
      {hint}
    </div>
  );
}
